import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { SessionModalComponent, SessionModalData } from './components/auth/session-modal/session-modal.component';
import { MsalLoadingComponent } from './components/auth/msal-loading/msal-loading.component';
import { SessionManagerService } from './services/session-manager.service';
import { MsalInitService } from './services/msal-init.service';
import { MsalRedirectHandlerService } from './services/msal-redirect-handler.service';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { SidebarService } from './services/sidebar.service';
import { AuthService } from './services/auth.service';
import { PermissionService } from './services/permission.service';
import { PageLoadingService } from './services/page-loading.service';
import { Subject, takeUntil, filter, Observable, combineLatest, map, startWith, switchMap, take, firstValueFrom } from 'rxjs';
import { MsalBroadcastService } from '@azure/msal-angular';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    SessionModalComponent,
    MsalLoadingComponent,
    MatIconModule,
    MatDialogModule
  ],
  template: `
    <!-- Splash / Loading Screen -->
    <app-msal-loading
      *ngIf="showLoadingScreen"
      [phase]="loadingPhase"
      [hasError]="initError"
      [isReady]="appReady"
      (fadeComplete)="showLoadingScreen = false"
      (retryClicked)="retryInit()">
    </app-msal-loading>

    <!-- Main Application (renders in background, covered by splash until ready) -->
    <div class="app-container"
         [class.authenticated]="showSidebar$ | async"
         [class.sidebar-collapsed]="isCollapsed$ | async"
         [class.chat-route]="isChatRoute">

      <app-sidebar *ngIf="(showSidebar$ | async) && !isChatRoute"></app-sidebar>

      <div class="main-content">
        <!-- Mobile Admin Top Bar -->
        <header class="mobile-admin-navbar" *ngIf="(showSidebar$ | async) && !isChatRoute">
          <div class="mobile-logo-container" (click)="navigateToHome()">
            <img src="https://cdaraucania.sfo2.cdn.digitaloceanspaces.com/01.resources/02.cdaraucania/01.img/da-logo.png"
                 alt="Desarrolla Araucanía"
                 class="mobile-logo">
          </div>
          <button class="mobile-menu-btn" (click)="toggleSidebar()">
            <mat-icon>{{ (isCollapsed$ | async) ? 'menu' : 'close' }}</mat-icon>
          </button>
        </header>

        <app-header *ngIf="!(isAdminRoute$ | async)"></app-header>

        <main class="content-area">
          <div class="page-overlay" [class.active]="pageLoading$ | async">
            <div class="page-spinner"></div>
          </div>
          <!-- Only mount route components after MSAL + auth + permissions are ready -->
          <router-outlet *ngIf="appReady"></router-outlet>
        </main>

        <app-footer *ngIf="!(isAdminRoute$ | async)"></app-footer>
      </div>

      <!-- Mobile Overlay Backdrop -->
      <div class="mobile-overlay-backdrop"
           *ngIf="(showSidebar$ | async) && !(isCollapsed$ | async)"
           (click)="toggleSidebar()">
      </div>

      <!-- Session Modal -->
      <app-session-modal
        [isVisible]="showSessionModal"
        [data]="sessionModalData"
        (modalClosed)="onModalClosed()"
        (actionPerformed)="onModalAction($event)">
      </app-session-modal>
    </div>
  `,
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Desarrolla Araucanía';
  showSessionModal = false;
  sessionModalData: SessionModalData | null = null;

  /** Loading screen stays in DOM until the fade-out animation completes */
  showLoadingScreen = true;
  /** Triggers the fade-out animation in MsalLoadingComponent */
  appReady = false;
  /** Shows the error panel inside MsalLoadingComponent */
  initError = false;
  /** Current loading phase passed to MsalLoadingComponent */
  loadingPhase: 'init' | 'auth' | 'permissions' | 'done' = 'init';

  isAuthenticated$ = this.authService.isAuthenticated$;
  isCollapsed$ = this.sidebarService.isCollapsed$;

  isAdminRoute$: Observable<boolean> = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map((event: any) => event.urlAfterRedirects.includes('/admin/')),
    startWith(this.router.url.includes('/admin/'))
  );

  /**
   * Immersive routes render their own full-screen header (with a "Volver" button) instead of
   * the shared admin sidebar/topbar — Chat Libre and Workstation are both full-screen surfaces
   * with their own back-navigation, so neither should show the surrounding admin chrome.
   */
  get isChatRoute(): boolean {
    const path = window.location.pathname;
    const isChatLibre = path.includes('/admin/chat') && !path.includes('/admin/chat-');
    const isWorkstation = path.includes('/admin/workstation');
    return isChatLibre || isWorkstation;
  }

  showSidebar$ = combineLatest([
    this.isAuthenticated$,
    this.isAdminRoute$
  ]).pipe(
    map(([isAuth, isAdmin]) => isAuth && isAdmin)
  );

  pageLoading$ = this.pageLoadingService.loading$;

  private destroy$ = new Subject<void>();

  constructor(
    private sessionManager: SessionManagerService,
    private msalInitService: MsalInitService,
    private redirectHandler: MsalRedirectHandlerService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private pageLoadingService: PageLoadingService,
    private sidebarService: SidebarService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      // Ignore router events that fire before the app is ready (initial bootstrap navigation)
      if (!this.appReady) return;

      if (event instanceof NavigationStart) {
        this.pageLoadingService.navigationStart();
        this.dialog.closeAll();
        Swal.close();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.pageLoadingService.navigationEnd();
      }
    });

    this.checkMsalInitialization();
    this.handleMobileState(window.innerWidth);
    window.addEventListener('resize', (e: any) => this.handleMobileState(e.target.innerWidth));

    this.sessionManager.sessionWarning$.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.sessionModalData = data as SessionModalData | null;
      this.showSessionModal = !!data;
    });
  }

  private handleMobileState(width: number): void {
    if (width <= 968) this.sidebarService.setCollapsed(true);
  }

  private async checkMsalInitialization(): Promise<void> {
    try {
      // ── Phase 1: Initialize MSAL ──────────────────────────────────────────
      this.loadingPhase = 'init';
      if (!this.msalInitService.isInitialized()) {
        await this.msalInitService.getMsalInstance();
      }

      // ── Phase 2: Determine auth state ────────────────────────────────────
      this.loadingPhase = 'auth';
      const isAuthenticated = await firstValueFrom(
        this.authService.isInitialized$.pipe(
          filter(ready => ready),
          take(1),
          switchMap(() => this.authService.isAuthenticated$),
          take(1)
        )
      );

      // ── Phase 3: Wait for permissions (only when authenticated) ──────────
      if (isAuthenticated) {
        this.loadingPhase = 'permissions';
        await firstValueFrom(
          this.permissionService.permissionsLoaded$.pipe(
            filter(loaded => loaded),
            take(1)
          )
        );
      }

      // ── All ready — trigger splash fade-out ──────────────────────────────
      this.loadingPhase = 'done';

      // Start page overlay before router-outlet appears so the first component's
      // HTTP requests are tracked and the content area stays covered until data loads.
      // navigationEnd() fires after a short delay to let the component's HTTP requests register.
      this.pageLoadingService.navigationStart();
      setTimeout(() => this.pageLoadingService.navigationEnd(), 80);

      this.appReady = true;

      // SSO sync: if the user explicitly clicked "Sincronizar Sesión"
      if (sessionStorage.getItem('explicit_sso_sync') === 'true') {
        sessionStorage.removeItem('explicit_sso_sync');
        if (!this.authService.isAuthenticated() && document.cookie.includes('cda_sso_token=')) {
          const match = document.cookie.match(/(?:^|; )cda_sso_token=([^;]+)/);
          let loginHint: string | undefined;
          if (match?.[1]) {
            try {
              const payload = JSON.parse(atob(match[1].split('.')[1]));
              loginHint = payload.preferred_username || payload.email || payload.upn;
            } catch {}
          }
          this.authService.loginRedirect({ loginHint });
        }
      }

    } catch (error) {
      console.error('App initialization failed:', error);
      this.initError = true;
    }
  }

  retryInit(): void {
    this.initError = false;
    this.appReady = false;
    this.loadingPhase = 'init';
    this.checkMsalInitialization();
  }

  toggleSidebar(): void { this.sidebarService.toggleSidebar(); }

  navigateToHome(): void {
    this.router.navigate(['/']);
    if (window.innerWidth <= 968) this.sidebarService.setCollapsed(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onModalClosed(): void {
    this.showSessionModal = false;
    this.sessionModalData = null;
    this.sessionManager.dismissModal();
  }

  onModalAction(action: string): void {
    switch (action) {
      case 'token-refreshed': break;
      case 'logout': break;
      case 'refresh-failed': break;
    }
  }
}
